import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useTextFontOverrides, useHeadingFontOverrides } from 'utils/font-override';
import { buildCssVar } from 'utils/style-override';
import { buildImgProps } from 'utils/media';

import type { ComponentsDetailListItem, ComponentsDetailListItemStyle } from 'queries/graphql';

import DetailListModal from 'generic/DetailListModal';

const MarkdownText = dynamic(() => import('generic/MarkdownText'));

import styles from './styles.module.scss';

type DetailListItemProps = ComponentsDetailListItem & {
  index: number;
};

export default function DetailListItem({
  index,
  image,
  title,
  subtitle,
  text,
  modalLinkText,
  modalTitle,
  modalText,
  itemStyle,
}: DetailListItemProps & {
  itemStyle?: ComponentsDetailListItemStyle;
}) {
  const { headingColor, textColor, headingFont, textFont } = itemStyle || {};

  const headingFontOverrides = useHeadingFontOverrides(headingFont);
  const textFontOverrides = useTextFontOverrides(textFont);
  const directionClass = index % 2 === 0 ? styles.image_left : styles.image_right;

  return (
    <div className={`root ${styles.container} ${directionClass}`}>
      <style jsx>{`
        .root {
          ${buildCssVar('--heading-color', headingColor, 'var(--heading-color-dark)')}
          ${buildCssVar('--text-color', textColor, 'var(--text-color-dark)')}
          ${headingFontOverrides ?? ''}
          ${textFontOverrides ?? ''}
        }
      `}</style>

      <div className={styles.image_container}>
        <div className={styles.image_wrapper}>
          <Image {...buildImgProps(image)} alt={image?.alt} />
        </div>
      </div>
      <div className={styles.detail}>
        {title && <h2 className={styles.heading}>{title}</h2>}
        {subtitle && <h3 className={styles.subheading}>{subtitle}</h3>}
        {text && (
          <div className={styles.text}>
            <MarkdownText text={text} />
          </div>
        )}
        {modalLinkText && (
          <DetailListModal
            linkText={modalLinkText}
            title={modalTitle}
            text={modalText}
            headingColor={headingColor}
            textColor={textColor}
            headingFont={headingFont}
            textFont={textFont}
          />
        )}
      </div>
    </div>
  );
}
